import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';

import { dayColor, getHeadObj } from 'lib/commonLib';
import {
  abusingFileReport,
  cnvrsAbusingTpCodeList,
  abusingCountGraph,
} from 'lib/api/abusing';

import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import PercentBarChart from 'components/organisms/Chart/PercentBarChart';

const useStyles = makeStyles((theme) => ({
  abusingBtnWrap: {
    display: 'flex',
    '& > div': {
      marginRight: 5,
      '&:last-child': {
        marginRight: 0,
      },
      '& > .active': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
      },
    },
  },
  infoWrap: {
    display: 'flex',
    '& ul': {
      width: '50%',
      paddingLeft: 0,
      '&:first-child': {
        marginRight: 10,
      },
    },
    '& li': { listStyle: 'none' },
  },
  infoTitle: {
    color: theme.palette.common.red,
  },
}));

const FilterIpStatistics = ({ handleClickSnackbar }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [initialization, setInitialization] = useState(false);

  const initParams = {
    sort: 'desc',
    order: 'STATS_DTTM',
    cnvrsAbusingTpCode: 'all',
    sDate: moment().add(-7, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
  };
  const [tempParams, setTempParams] = useState(initParams);
  const [params, setParams] = useState(initParams);
  const initList = [];
  const [list, setList] = useState(initList);
  const [graph, setGraph] = useState({
    cnvrsAbusingTpCode: {
      title: 'title',
      data: [],
      legend: [],
    },
  });

  const [filterLoading, setFilterLoading] = useState(true);
  const [percentGraphLoading, setPercentGraphLoading] = useState(true);

  useEffect(() => {
    cnvrsAbusingTpCodeList()
      .then((response) => {
        const { data } = response.data;
        setList({
          ...list,
          cnvrsAbusingTpCode: data,
        });
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setFilterLoading(false);
      }); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPercentGraphLoading(true);
    abusingCountGraph({
      sDate: params.sDate,
      eDate: params.eDate,
      uri: params.uri,
    })
      .then((response) => {
        const { data } = response.data;
        setGraph({
          ...graph,
          cnvrsAbusingTpCode: {
            ...graph.cnvrsAbusingTpCode,
            title: '어뷰징 정책',
            data: data.CNT.data,
            label: data.CNVRS_ABUSING_TP_CODE.data,
          },
        });
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setPercentGraphLoading(false);
      }); // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  const customized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        const statsDttm = getHeadObj(headsMap, 'STATS_DTTM');
        return (
          rowObj['STATS_DTTM'] !== '' &&
          dayColor(rowObj['STATS_DTTM'], statsDttm.type, statsDttm.format)
        );
      },
    },
    INFLOW_ROUTE: {
      makeHead: (headsMap) => {
        return (
          <div style={{ width: 200 }}>
            {getHeadObj(headsMap, 'INFLOW_ROUTE').label}
          </div>
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <div
            style={{
              width: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'noWrap',
            }}
          >
            {rowObj['INFLOW_ROUTE']}
          </div>
        );
      },
    },
    SCRIPT_NAME: {
      makeBody: (rowObj, headsMap) => {
        return (
          <div
            style={{
              width: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'noWrap',
              textAlign: 'left',
            }}
          >
            {rowObj['SCRIPT_NAME']}
          </div>
        );
      },
    },
    PNM: {
      makeBody: (rowObj, headsMap) => {
        return (
          <div
            style={{
              width: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'noWrap',
              textAlign: 'left',
            }}
          >
            {rowObj['PNM']}
          </div>
        );
      },
    },
  };

  const getTempsParam = (newParams) => {
    if (JSON.stringify(tempParams) !== JSON.stringify(newParams)) {
      setTempParams(newParams);
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   */
  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
    }
  };

  /**
   * 필터 초기화
   * initialization 필터 내부에서 초기화 버튼이 클릭되었을 때 기본값으로 돌려주는 함수를 실행시킴.
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
    }
  };

  return (
    <>
      <NewFilterWrap
        loading={filterLoading || percentGraphLoading}
        handleOnReset={handleOnReset}
      >
        <NewCommonFilter
          initialization={initialization}
          getParam={getTempsParam}
          params={tempParams}
          useDisplayFilter={false}
          useExternalFilter={false}
          usePltfomTpCodeFilter={false}
        />
        <NewButton type="filterButton" onClick={getParam} disableRipple>
          {t('검색')}
        </NewButton>
      </NewFilterWrap>
      <SwitchingArea title={t('어뷰징 정책')}>
        <div className={classes.abusingBtnWrap}>
          {!percentGraphLoading &&
            (() => {
              const { data } = graph.cnvrsAbusingTpCode;
              let total = 0;
              for (let item of data) {
                total = total + item.val;
              }
              let result = [
                <NewButton
                  key="total"
                  type="filterButton"
                  className={clsx(
                    params.cnvrsAbusingTpCode === 'all' && 'active'
                  )}
                  onClick={() =>
                    setParams({
                      ...params,
                      cnvrsAbusingTpCode: 'all',
                    })
                  }
                >
                  {t('전체')} / {numeral(total).format() + t('건')}
                </NewButton>,
              ];
              for (let i = 0; data.length > i; i++) {
                let itemKey;
                for (const prop of list.cnvrsAbusingTpCode) {
                  if (prop['CODE_VAL'] === data[i].name)
                    itemKey = prop['CODE_ID'];
                }
                result.push(
                  <NewButton
                    key={i}
                    type="filterButton"
                    className={clsx(
                      params.cnvrsAbusingTpCode === itemKey && 'active'
                    )}
                    onClick={() =>
                      setParams({
                        ...params,
                        cnvrsAbusingTpCode: itemKey,
                      })
                    }
                  >
                    {data[i].name} / {numeral(data[i].val).format() + t('건')}
                  </NewButton>
                );
              }
              return result;
            })()}
        </div>
        <PercentBarChart
          loading={percentGraphLoading}
          data={graph.cnvrsAbusingTpCode}
        />
        <div className={classes.infoWrap}>
          <ul>
            <li>
              <span className={classes.infoTitle}>세션 매출 : </span> 창 안닫힌
              상태에서 6시간 내 같은 상품 11건 이하 구매시 인정, 12건 부터는
              직접 매출
            </li>
            <li>
              <span className={classes.infoTitle}>직접 매출 : </span>{' '}
              광고주설정시간(24시간) 내 같은 상품 15건 이하 구매시 인정, 16건
              부터는 간접 매출
            </li>
            <li>
              <span className={classes.infoTitle}>간접 매출 : </span>{' '}
              광고주설정시간(30일) 내 같은 상품 19건 이하 구매시 인정, 20건
              부터는 무효처리
            </li>
            <li>
              <span className={classes.infoTitle}>1억원 : </span>주문 금액이
              1억원 이상인 컨버전 무효 처리
            </li>
          </ul>
          <ul>
            <li>
              <span className={classes.infoTitle}>상품 : </span>광고 클릭
              시간으로 부터 컨버전 5초 이내인 건 무효
            </li>
            <li>
              <span className={classes.infoTitle}>비상품 : </span>광고 클릭
              시간으로 부터 컨버전 10초 이내이면서 주문금액이 1원인 건 무효
            </li>
            <li>
              <span className={classes.infoTitle}>1분이내 : </span>동일IP, 동일
              상품, 동일가격으로 1분 이내 2건 이상 발생 건 1건으로 인정
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;전화연결광고와
              같이 특수 광고 또는 광고주와 영업 협의 된 건 제외
            </li>
          </ul>
        </div>
      </SwitchingArea>
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={abusingFileReport}
        customized={customized}
        useToolBar={false}
      />
    </>
  );
};

export default FilterIpStatistics;
